import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../api";
import { useI18n } from "../i18n";
import { useCart } from "../cart";
import { useSEO } from "../seo";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => navigate("/shop"));
  }, [id, navigate]);

  useSEO(p ? {
    title: `${lang === "fr" ? p.name_fr : p.name} — Beaudoin`,
    description: lang === "fr" ? p.description_fr : p.description,
    image: p.image,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": p.name,
      "description": p.description,
      "image": [p.image],
      "brand": { "@type": "Brand", "name": "Beaudoin" },
      "category": p.category,
      "offers": {
        "@type": "Offer",
        "url": typeof window !== "undefined" ? window.location.href : "",
        "priceCurrency": "EUR",
        "price": p.price.toFixed(2),
        "availability": p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition"
      }
    }
  } : { title: "Beaudoin" });

  if (!p) return <div className="min-h-screen bg-black" />;

  const onAdd = () => {
    if (p.sizes.length > 1 && !size) {
      setError(t.product.select_size);
      return;
    }
    add(p, size || p.sizes[0] || null);
    setError("");
  };

  return (
    <div className="bg-black text-white pt-28 pb-32 min-h-screen">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}
          className="md:col-span-7">
          <div className="aspect-[4/5] bg-[#0a0707] overflow-hidden relative">
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="md:col-span-5 md:sticky md:top-32 self-start">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-4">{p.category}</div>
          <h1 className="font-black text-4xl md:text-5xl leading-tight tracking-tight uppercase mb-4">
            {lang === "fr" ? p.name_fr : p.name}
          </h1>
          <div className="text-2xl font-medium text-bone mb-10">€{p.price.toFixed(2)}</div>

          <div className="border-y border-white/10 py-8 mb-8">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-4">{t.product.description}</div>
            <p className="text-white/70 leading-relaxed">{lang === "fr" ? p.description_fr : p.description}</p>
          </div>

          {p.sizes.length > 0 && (
            <div className="mb-8">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-4">{t.product.size}</div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button key={s} data-testid={`size-${s}`}
                    onClick={() => { setSize(s); setError(""); }}
                    className={`px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase border transition-colors ${
                      size === s ? "bg-white text-black border-white" : "border-white/20 hover:border-white"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <div className="text-blood text-sm mb-4" data-testid="product-error">{error}</div>}

          {p.sumup_link || p.shopify_variant_id ? (
            <button
              data-testid="buy-now-btn"
              onClick={async () => {
                try {
                  if (p.sizes.length > 0 && !size && p.sizes.length > 1) {
                    setError(t.product.select_size);
                    return;
                  }
                  const { data } = await api.post("/checkout/sumup", {
                    items: [{ product_id: p.id, quantity: 1, size: size || (p.sizes[0] || null) }],
                  });
                  window.location.href = data.hosted_checkout_url;
                } catch (e) {
                  setError(lang === "fr" ? "Erreur de paiement, réessayez." : "Payment error, please retry.");
                  console.error(e);
                }
              }}
              className="w-full bg-blood text-white py-5 text-xs font-bold tracking-[0.25em] uppercase hover:bg-blood-deep transition-colors"
            >
              {lang === "fr" ? "Acheter maintenant" : "Buy now"} — €{p.price.toFixed(2)}
            </button>
          ) : (
            <button data-testid="add-to-cart-btn" onClick={onAdd}
              className="w-full bg-blood text-white py-5 text-xs font-bold tracking-[0.25em] uppercase hover:bg-blood-deep transition-colors">
              {t.product.add}
            </button>
          )}

          <div className="mt-8 text-xs text-white/40 leading-relaxed">
            Limited series · Sterling 925 silver where applicable · Hand-finished in Saint Barth · Insured worldwide express shipping
          </div>
        </motion.div>
      </div>
    </div>
  );
}
